<template>
  <div ref="edRef" class="ed" :class="{ 'sheet-open': !!sheet }" @keydown="onKey">
    <!-- ── Barra: entidad · activa · cuándo · requisitos · acciones ─────────────── -->
    <div class="ed-bar" :inert="!!sheet">
      <span class="ent-id" :class="{ none: !dto.rootEntity }" :title="dto.rootEntity">
        <span class="mc-ico entity">E</span>
        <b class="mono">{{ dto.rootEntity ? comboName(dto.rootEntity) : t('ed.noEntity') }}</b>
        <span v-if="dto.rootEntity" class="mono sm muted">{{ comboTables(dto.rootEntity) }}</span>
      </span>
      <span class="sep" />
      <button type="button" class="tgl" :class="{ on: dto.enabled }" @click="dto.enabled = !dto.enabled">
        <span class="tgl-track"><span class="tgl-knob" /></span>
        {{ dto.enabled ? t('ed.enabled') : t('ed.disabled') }}
      </button>
      <span class="sep" />
      <TriggerChip ref="triggerRef" :dto="dto" />
      <span class="grow" />
      <span v-if="dirty" class="chip warn">{{ t('ed.unsaved') }}</span>
      <span v-else-if="dto.runRequested" class="chip info">{{ t('state.pendingRun') }}</span>
      <DefPopover ref="defRef" :dto="dto" :can-delete="!!dto.id" @change-entity="startChangeEntity" @delete="doDelete" />
      <ReadyCheck ref="readyRef" :items="checks" :server="serverProblems" :notes="notes" @go="goTo" />
      <button type="button" class="btn ghost" :disabled="!!busy || !isReady" :title="isReady ? '' : t('chk.runBlocked')"
              @click="doRun">{{ t('ed.run') }}</button>
      <button type="button" class="btn primary" :disabled="!!busy || !dto.rootEntity" @click="doSave">{{ t('ed.save') }}</button>
    </div>
    <Transition name="vem-fold"><div v-if="notice" class="notice" :class="notice.type">{{ notice.text }}</div></Transition>

    <!-- Alto = lo que queda de ventana: carril y lienzo hacen scroll/zoom dentro, sin desbordar. -->
    <div ref="gridRef" class="ed-grid" :style="{ height: gridH + 'px' }" :inert="!!sheet">
      <EntityPicker v-if="!dto.rootEntity || picking" ref="pickerRef" :current="dto.rootEntity" :def-id="dto.id"
                    :cancelable="picking && !!dto.rootEntity" @pick="pickEntity" @cancel="picking = false" />
      <template v-else>
        <SourceRail ref="railRef" :sources="dto.sources" :connected="connectedAliases" :open="railOpen" :selected="shownForm"
                    :highlight="railHighlight" @toggle="onRailToggle" @select="selForm = $event"
                    @hover="railHover = $event" @update="onSourceUpdate" @add="onSourceAdd" @move="onSourceMove" @remove="onSourceRemove" />
        <div ref="boxRef" class="canvas-box">
          <div class="cv-tools">
            <label class="cv-search">
              <input v-model="view.query" class="in" :placeholder="t('cv.search')" :aria-label="t('cv.search')"
                     @keydown.enter.prevent="focusFirstMatch" @keydown.esc.stop="view.query = ''" />
            </label>
            <span v-if="view.query.trim()" class="cv-count">{{ t('cv.matches', { n: canvas.matches }) }}</span>
            <button type="button" class="cv-btn" :class="{ on: view.onlyWarnings }" :aria-pressed="view.onlyWarnings"
                    :disabled="!canvas.warnCount && !view.onlyWarnings" @click="view.onlyWarnings = !view.onlyWarnings">
              ▲ {{ t('cv.onlyWarnings', { n: canvas.warnCount }) }}
            </button>
            <span class="grow" />
            <div class="lg-wrap" style="position: relative">
              <button ref="legendBtn" type="button" class="cv-btn" :aria-expanded="legendOpen" @click="legendOpen = !legendOpen">? {{ t('cv.legend') }}</button>
              <Transition name="vem-pop">
              <div v-if="legendOpen" class="pop cv-legend" role="dialog" :aria-label="t('cv.legend')">
                <ul>
                  <li><span class="lg-k mc-cov"><i style="--fc: var(--fc0)">A</i><i class="off">B</i><i class="dis">C</i></span>{{ t('lg.on') }} · {{ t('lg.off') }} · {{ t('lg.dis') }}</li>
                  <li><span class="lg-k mc-covk">7/40<span class="mc-bar"><b style="width: 60%" /></span></span>{{ t('lg.bar') }}</li>
                  <li><span class="lg-k"><svg width="44" height="8"><path d="M0 4 H44" class="mc-edge" /></svg></span>{{ t('lg.line') }}</li>
                  <li><span class="lg-k"><svg width="44" height="8"><path d="M0 4 H44" class="mc-edge sys" /></svg></span>{{ t('lg.sys') }}</li>
                  <li><span class="lg-k"><svg width="44" height="8"><path d="M0 4 H44" class="mc-edge dead" /></svg></span>{{ t('lg.dead') }}</li>
                  <li><span class="lg-k mc-order"><svg width="18" height="18"><circle cx="9" cy="9" r="7" /><text x="9" y="9">1</text></svg></span>{{ t('lg.order') }}</li>
                  <li><span class="lg-k"><span class="mc-tag key">{{ t('chip.keyId') }}</span></span>{{ t('lg.key') }}</li>
                  <li><span class="lg-k"><span class="mc-st warn" /><span class="mc-st danger" /></span>{{ t('lg.status') }}</li>
                </ul>
              </div>
              </Transition>
            </div>
          </div>
          <MapCanvas ref="canvasRef" :nodes="canvas.nodes" :edges="canvas.edges" :focus="focus" :lens="lens"
                     @connect="onConnect" @open-field="openField" @edge-menu="openMenu" @toggle-group="toggleGroup"
                     @hover-row="onHoverRow" @drag="view.dragging = $event" @action="onCanvasAction" />
          <Transition name="vem-pop">
          <div v-if="peek" class="cv-peek" aria-hidden="true">
            <b>{{ peek.title }}</b> · {{ peek.text }}
            <span v-for="(l, i) in peek.lines" :key="i" class="cv-peek-sub">{{ l }}</span>
          </div>
          </Transition>
          <Transition name="vem-pop"><div v-if="emptyHint" class="cv-hint">{{ emptyHint }}</div></Transition>
          <Transition name="vem-pop">
          <EdgeMenu v-if="menu && menuData" v-bind="menuData" @close="closeMenu" @rule="onMenuRule" @up="onMenuMove(-1)"
                    @down="onMenuMove(1)" @first="onMenuMove('first')" @replace="onMenuReplace" @move="onMenuRetarget"
                    @copy="onMenuCopy" @fallback="onMenuFallback" @forms="railHighlight = $event ? menuForms : null"
                    @open="openFieldFromMenu" @remove="onMenuRemove" />
          </Transition>
        </div>
      </template>
    </div>

    <Transition name="fs" :duration="180">
      <FieldSheet v-if="sheet && sheetData" v-bind="sheetData" @close="closeSheet" @nav="navSheet"
                  @rule="(p) => setFieldRule(sheet, p)" @reorder="onSheetReorder" @add="onSheetAdd" @replace="onSheetReplace"
                  @remove="onSheetRemove" @clear-all="onSheetClearAll" @revert="onSheetRevert" @edit-source="onEditSource" />
    </Transition>

    <Transition name="vem-pop">
    <div v-if="toast" class="toast" role="status">
      <span>{{ toast.text }}</span>
      <button v-if="toast.undo" type="button" @click="undo">{{ t('toast.undo') }}</button>
      <button v-if="toast.redo" type="button" @click="redo">{{ t('toast.redo') }}</button>
      <button v-if="toast.open" type="button" @click="openField(toast.open)">{{ t('toast.open') }}</button>
      <button v-if="toast.view" type="button" @click="toast.view()">{{ t('toast.view') }}</button>
      <button type="button" class="toast-x" :aria-label="t('ed.close')" @click="toast = null">×</button>
    </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import MapCanvas from '@vem/components/MapCanvas.vue';
import ReadyCheck from '@vem/components/ReadyCheck.vue';
import SourceRail from '@vem/components/SourceRail.vue';
import EntityPicker from '@vem/components/EntityPicker.vue';
import TriggerChip from '@vem/components/TriggerChip.vue';
import DefPopover from '@vem/components/DefPopover.vue';
import EdgeMenu from '@vem/components/EdgeMenu.vue';
import FieldSheet from '@vem/components/FieldSheet.vue';
import { t } from '@vem/i18n.js';
import {
  aliasLabel, catalog, comboName, comboTables, entityByClass, extensionsOf, formAliasSets, formName, keyRoleOf,
  loadCatalog, shortName,
} from '@vem/catalog.js';
import * as ops from '@vem/mapOps.js';
import { useHistory } from '@vem/history.js';
import { buildRecipe, isSys, suggestAliases } from '@vem/recipe.js';
import { aliasText, buildCanvas, coverage, formBadge, orderedSources, orphanAliases } from '@vem/canvasModel.js';
import { advisories, readiness } from '@vem/readiness.js';
import { deleteDef, getDef, runDef, saveDef } from '@vem/api/materializeApi.js';

const props = defineProps({ defId: { type: String, default: null } });
const emit = defineEmits(['back', 'saved', 'title']);

const SYS_KEY = { '@quest_id': 'quest_id', '@response_id': 'response_id', '@response_date': 'response_date' };
const PEEK_MS = 350;
const TOAST_MS = 6000;

const dto = reactive({
  id: null, name: '', description: '', rootEntity: '', triggerType: 'interval', triggerCron: '0 0 3 * * *',
  triggerEvery: 1, triggerUnit: 'h', enabled: false, sources: [], fields: [], maps: [], runRequested: false,
});
// Cambios sin guardar = lo que se guardaría difiere de lo último cargado/guardado.
const saved = ref(null);
const dirty = computed(() => saved.value !== null && !loading.value && JSON.stringify(toPayload()) !== saved.value);
const loading = ref(true);
const busy = ref('');
const notice = ref(null);
const edRef = ref(null);
const gridRef = ref(null);
const gridH = ref(560);
// Problemas que devolvió el servidor en el último guardado (se descartan al seguir editando).
const serverProblems = ref([]);
const canvasRef = ref(null);
const readyRef = ref(null);
const triggerRef = ref(null);
const defRef = ref(null);
const railRef = ref(null);
const pickerRef = ref(null);
const boxRef = ref(null);

// Estado de vista: nunca va al payload (no marca «sin guardar»).
const view = reactive({ fieldsOpen: false, aliasesOpen: false, onlyWarnings: false, query: '', dragging: false });
const sheet = ref(null); // campo abierto en la hoja (targetKey o key:id / key:origin)
const sheetOrigin = ref(null);
let sheetSnap = null;
const menu = ref(null); // { index, x, y } (x/y en .canvas-box)
const railOpen = ref(null);
const railHover = ref(null);
const railHighlight = ref(null);
const picking = ref(false);
const legendOpen = ref(false);
const peek = ref(null);
const toast = ref(null);
let peekTimer = 0;
let toastTimer = 0;

const history = useHistory(() => ({ sources: dto.sources, fields: dto.fields, maps: dto.maps }), (s) => Object.assign(dto, s));
const mutate = (label, fn) => history.mutate(label, fn);

// ── Derivados ────────────────────────────────────────────────────────────────
const checks = computed(() => readiness(dto));
const isReady = computed(() => checks.value.every((c) => c.ok) && !serverProblems.value.length);
const sourceIds = computed(() => orderedSources(dto.sources).map((s) => s.questId));
const labelOf = (alias) => aliasText(alias, dto.sources);

/** Campos de la entidad (raíz, extensiones y clases antiguas con conexiones). */
const entityFields = computed(() => {
  const root = dto.rootEntity;
  if (!root) return new Map();
  const classes = [root, ...extensionsOf(root)];
  const legacy = [...new Set(dto.maps.map((m) => m.entityClass))].filter((c) => !classes.includes(c));
  const out = new Map();
  for (const cls of [...classes, ...legacy]) {
    const e = entityByClass(cls);
    for (const f of e ? e.fields : []) {
      const id = ops.targetKey(cls, f.name);
      out.set(id, { id, cls, name: f.name, type: f.type, column: f.column, table: e.table, isExt: cls !== root,
        rootName: shortName(root), keyRole: keyRoleOf(root, cls, f.name) });
    }
  }
  return out;
});

const chainOf = (cls, field) => dto.maps.filter((m) => m.entityClass === cls && m.fieldName === field).map((m) => m.sourceAlias);

function recipeFor(f) {
  return buildRecipe({
    field: f, rule: ops.ruleOf(dto, f.cls, f.name), chain: f.keyRole ? [] : chainOf(f.cls, f.name), sources: dto.sources,
    formAliases: (q) => formAliasSets.value.get(q) || null, formName, aliasLabel: (a) => aliasLabel(a, sourceIds.value),
  });
}

/** Receta de los campos conectados y de los de la clave (los demás no se tocan). */
const recipes = computed(() => {
  const mapped = new Set(dto.maps.map((m) => ops.targetKey(m.entityClass, m.fieldName)));
  const out = new Map();
  entityFields.value.forEach((f, id) => {
    if (mapped.has(id) || f.keyRole) out.set(id, recipeFor(f));
  });
  return out;
});

// Formulario cuyas preguntas pinta el lienzo: el último que se abrió en el carril, o el primero
const selForm = ref(null);
watch(railOpen, (id) => { if (id) selForm.value = id; });
const shownForm = computed(() => (dto.sources.some((s) => s.questId === selForm.value) ? selForm.value : orderedSources(dto.sources)[0]?.questId || null));
function onRailToggle(id) {
  selForm.value = id;
  railOpen.value = railOpen.value === id ? null : id;
}
const canvas = computed(() => (dto.rootEntity ? buildCanvas({ dto, recipes: recipes.value, view, form: shownForm.value })
  : { nodes: [], edges: [], fieldOrder: [], warnCount: 0, matches: 0 }));
const notes = computed(() => advisories(dto, recipes.value));
const connectedAliases = computed(() => {
  const orphans = orphanAliases(dto);
  return new Set(dto.maps.map((m) => m.sourceAlias).filter((a) => !orphans.has(a)));
});
const emptyHint = computed(() => {
  if (!dto.rootEntity || view.onlyWarnings) return '';
  if (!dto.sources.length) return t('cv.noSourcesHint');
  return dto.maps.length ? '' : t('cv.emptyHint');
});

/** Recorrido de un campo (filas y aristas) para iluminarlo. */
function routeOf(fieldId) {
  const rows = new Set(['entity|' + fieldId]);
  const edges = new Set();
  for (const e of canvas.value.edges) {
    if (e.to.row === fieldId) {
      edges.add(e.key);
      rows.add('aliases|' + e.from.row);
    }
  }
  return { rows, edges };
}

const focus = computed(() => {
  if (sheet.value) return sheet.value.startsWith('key:') ? { rows: new Set(['entity|' + sheet.value]), edges: new Set() } : routeOf(sheet.value);
  if (menu.value) {
    const m = dto.maps[menu.value.index];
    if (!m) return null;
    const e = canvas.value.edges.find((x) => x.index === menu.value.index);
    return { rows: new Set(['aliases|' + m.sourceAlias, 'entity|' + ops.targetKey(m.entityClass, m.fieldName)]), edges: new Set(e ? [e.key] : []) };
  }
  return null;
});

/** Lente de un formulario del carril: sus preguntas, sus líneas y los campos que alimenta. */
const lens = computed(() => {
  const q = railHover.value;
  const set = q && formAliasSets.value.get(q);
  if (!set) return null;
  const rows = new Set();
  const edges = new Set();
  for (const e of canvas.value.edges) {
    if (isSys(e.from.row) || set.has(e.from.row)) {
      edges.add(e.key);
      rows.add('aliases|' + e.from.row).add('entity|' + e.to.row);
    }
  }
  return { rows, edges };
});

// ── Opciones de los selectores ───────────────────────────────────────────────
function fieldNames(alias) {
  return dto.maps.filter((m) => m.sourceAlias === alias).map((m) => m.fieldName);
}

/** Preguntas para un campo: sugeridas, datos de la encuesta y preguntas de las fuentes. */
function aliasOptions(fieldId) {
  const f = entityFields.value.get(fieldId);
  if (!f) return [];
  const here = new Set(chainOf(f.cls, f.name));
  const pool = new Set();
  dto.sources.filter((s) => s.enabled).forEach((s) => (formAliasSets.value.get(s.questId) || []).forEach((a) => pool.add(a)));
  dto.maps.forEach((m) => { if (!isSys(m.sourceAlias)) pool.add(m.sourceAlias); });
  const opt = (alias, group) => {
    const cov = coverage(alias, dto.sources);
    const used = fieldNames(alias).filter((n) => n !== f.name);
    return {
      value: alias, label: labelOf(alias), sub: isSys(alias) ? t('sys.' + SYS_KEY[alias], { code: sourceIds.value[0] || 'SUR-0000000434' }) : alias,
      meta: isSys(alias) ? '' : t('fs.carried', { k: cov.k, N: cov.N }), group, disabled: here.has(alias),
      hint: here.has(alias) ? t('pk.here') : used.length ? t('pk.used', { fields: used.join(', ') }) : '',
    };
  };
  const list = [...pool].map((a) => ({ alias: a, label: labelOf(a) }));
  const sugg = suggestAliases(f.name, list).filter((a) => !here.has(a));
  return [
    ...sugg.map((a) => opt(a, t('fs.suggested'))),
    ...catalog.systemAliases.map((a) => opt(a, t('cv.sysGroup'))),
    ...list.filter((x) => !sugg.includes(x.alias)).sort((a, b) => a.label.localeCompare(b.label)).map((x) => opt(x.alias, t('pk.questions'))),
  ];
}

/** Campos de destino agrupados por clase; la clave y el actual, inactivos. */
function fieldOptions(excludeId) {
  const out = [];
  entityFields.value.forEach((f, id) => {
    const e = entityByClass(f.cls);
    const r = ops.ruleOf(dto, f.cls, f.name);
    out.push({
      value: id, label: f.name, sub: f.type, group: [e ? e.name : shortName(f.cls), e?.table].filter(Boolean).join(' · '),
      meta: chainOf(f.cls, f.name).length ? t('chip.' + (r ? r.mergeMode : 'last')) : '',
      disabled: !!f.keyRole || id === excludeId, hint: f.keyRole ? t('pk.key') : id === excludeId ? t('pk.current') : '',
    });
  });
  return out;
}

// ── Hoja del campo ───────────────────────────────────────────────────────────
const sheetField = computed(() => {
  const id = sheet.value;
  if (!id) return null;
  if (id.startsWith('key:')) {
    const e = entityByClass(dto.rootEntity);
    const name = id === 'key:id' ? e?.sourceIdField : e?.originField;
    return entityFields.value.get(ops.targetKey(dto.rootEntity, name)) || null;
  }
  return entityFields.value.get(id) || null;
});

const sheetData = computed(() => {
  const f = sheetField.value;
  if (!f) return null;
  const id = f.id;
  const order = canvas.value.fieldOrder;
  const at = order.indexOf(sheet.value);
  const recipe = recipes.value.get(id) || recipeFor(f);
  return {
    field: f, recipe, rule: ops.ruleOf(dto, f.cls, f.name) || { mergeMode: 'last', onMissing: 'keep' },
    chain: f.keyRole ? [] : ops.chainIdx(dto, f.cls, f.name).map((index) => {
      const alias = dto.maps[index].sourceAlias;
      const cov = coverage(alias, dto.sources);
      return { index, alias, label: labelOf(alias), sys: isSys(alias), dead: !isSys(alias) && cov.N > 0 && cov.k === 0, cov };
    }),
    aliasOptions: f.keyRole ? [] : aliasOptions(id), pos: { i: at + 1, n: order.length }, saved: !!dto.id,
    origin: sheetOrigin.value,
  };
});

function openField(id) {
  if (id === 'key:entity') {
    const src = dto.sources.find((x) => x.enabled && x.keyMode === 'entity');
    if (src) goTo({ kind: 'form', id: src.questId, focus: 'idAlias' });
    return;
  }
  closeMenu();
  const key = id.startsWith('key:') ? id : id;
  const rect = canvasRef.value?.rowRect(key);
  const host = edRef.value?.getBoundingClientRect();
  sheetOrigin.value = rect && host ? { x: rect.left - host.left + rect.width / 2, y: rect.top - host.top + rect.height / 2 } : null;
  sheet.value = key;
  sheetSnap = history.snapshot();
  peek.value = null;
}

function closeSheet() {
  const id = sheet.value;
  sheet.value = null;
  nextTick(() => id && canvasRef.value?.focusRow(id));
}

function navSheet(d) {
  const order = canvas.value.fieldOrder;
  if (!order.length) return;
  const at = order.indexOf(sheet.value);
  const next = order[at + d];
  if (!next) return;
  sheet.value = next;
  sheetSnap = history.snapshot();
}

function setFieldRule(fieldId, patch) {
  const f = entityFields.value.get(fieldId) || sheetField.value;
  if (f) mutate(t('hist.rule'), () => ops.setRule(dto, f.cls, f.name, patch));
}

function onSheetReorder({ from, to }) {
  const f = sheetField.value;
  mutate(t('hist.reorder'), () => ops.moveInChain(dto, f.cls, f.name, from, to));
}

function onSheetAdd(alias) {
  const f = sheetField.value;
  mutate(t('hist.connect'), () => ops.connect(dto, alias, f.cls, f.name));
}

function onSheetReplace({ index, alias }) {
  const f = sheetField.value;
  const res = mutate(t('hist.replace'), () => ops.replaceAlias(dto, index, alias));
  if (res === 'merged') showToast({ text: t('toast.merged', { q: `«${labelOf(alias)}»`, field: f.name }), undo: true });
}

function onSheetRemove(index) {
  mutate(t('hist.remove'), () => ops.removeMap(dto, index));
  showToast({ text: t('toast.removed'), undo: true });
}

function onSheetClearAll() {
  const f = sheetField.value;
  const n = mutate(t('hist.removeAll'), () => ops.removeField(dto, f.cls, f.name));
  showToast({ text: t('toast.removedAll', { n, field: f.name }), undo: true });
}

function onSheetRevert() {
  if (!sheetSnap) return;
  history.restore(t('hist.revert'), sheetSnap);
  showToast({ text: t('toast.undone', { what: t('hist.revert') }), redo: history.canRedo() });
}

function onEditSource({ questId, field }) {
  closeSheet();
  railOpen.value = questId;
  nextTick(() => railRef.value?.focusSource(questId, field));
}

// ── Menú de una conexión ─────────────────────────────────────────────────────
const menuForms = computed(() => {
  const m = menu.value && dto.maps[menu.value.index];
  if (!m) return null;
  return new Set(dto.sources.filter((s) => formAliasSets.value.get(s.questId)?.has(m.sourceAlias)).map((s) => s.questId));
});

const menuData = computed(() => {
  const mn = menu.value;
  const m = mn && dto.maps[mn.index];
  if (!m) return null;
  const fieldId = ops.targetKey(m.entityClass, m.fieldName);
  const chain = ops.chainIdx(dto, m.entityClass, m.fieldName);
  const cov = coverage(m.sourceAlias, dto.sources);
  const box = boxRef.value?.getBoundingClientRect();
  return {
    x: mn.x, y: mn.y, bounds: { w: box ? box.width : 800, h: box ? box.height : 600 }, index: mn.index, alias: m.sourceAlias,
    label: labelOf(m.sourceAlias), field: { id: fieldId, name: m.fieldName, cls: m.entityClass },
    pos: { i: chain.indexOf(mn.index) + 1, n: chain.length }, rule: ops.ruleOf(dto, m.entityClass, m.fieldName) || { mergeMode: 'last', onMissing: 'keep' },
    line: recipes.value.get(fieldId)?.line || '',
    carried: isSys(m.sourceAlias) ? null : { k: cov.k, N: cov.N, forms: orderedSources(dto.sources).map((s) => ({ name: formName(s.questId), prio: s.priority || 1, ...formBadge(dto.sources, s.questId),
      on: isSys(m.sourceAlias) || !!formAliasSets.value.get(s.questId)?.has(m.sourceAlias) })) },
    aliasOptions: aliasOptions(fieldId), fieldOptions: fieldOptions(fieldId),
  };
});

function openMenu({ index, x, y }) {
  const box = boxRef.value?.getBoundingClientRect();
  if (!box) return;
  peek.value = null;
  menu.value = { index, x: x - box.left, y: y - box.top };
}

function closeMenu() {
  menu.value = null;
  railHighlight.value = null;
}

function menuMap() {
  return menu.value ? dto.maps[menu.value.index] : null;
}

function onMenuRule(patch) {
  const m = menuMap();
  if (m) mutate(t('hist.rule'), () => ops.setRule(dto, m.entityClass, m.fieldName, patch));
}

function onMenuMove(d) {
  const m = menuMap();
  if (!m) return;
  const chain = ops.chainIdx(dto, m.entityClass, m.fieldName);
  const from = chain.indexOf(menu.value.index);
  const to = d === 'first' ? 0 : from + d;
  if (to < 0 || to >= chain.length) return;
  mutate(t('hist.reorder'), () => ops.moveInChain(dto, m.entityClass, m.fieldName, from, to));
  menu.value = { ...menu.value, index: ops.chainIdx(dto, m.entityClass, m.fieldName)[to] };
}

function onMenuReplace(alias) {
  const m = menuMap();
  const res = mutate(t('hist.replace'), () => ops.replaceAlias(dto, menu.value.index, alias));
  if (res === 'merged') showToast({ text: t('toast.merged', { q: `«${labelOf(alias)}»`, field: m.fieldName }), undo: true });
  closeMenu();
}

function onMenuRetarget(fieldId) {
  const f = entityFields.value.get(fieldId);
  const m = menuMap();
  const res = mutate(t('hist.move'), () => ops.retarget(dto, menu.value.index, f.cls, f.name));
  showToast({ text: res === 'exists' ? t('toast.exists', { q: `«${labelOf(m.sourceAlias)}»`, field: f.name }) : t('toast.moved', { field: f.name }), undo: true, open: fieldId });
  closeMenu();
}

function onMenuCopy(fieldId) {
  const f = entityFields.value.get(fieldId);
  const m = menuMap();
  const res = mutate(t('hist.copy'), () => ops.copyTo(dto, menu.value.index, f.cls, f.name));
  showToast({ text: res === 'exists' ? t('toast.exists', { q: `«${labelOf(m.sourceAlias)}»`, field: f.name }) : t('toast.copied', { field: f.name }), undo: res !== 'exists', open: fieldId });
  closeMenu();
}

function onMenuFallback(alias) {
  const m = menuMap();
  const res = mutate(t('hist.fallback'), () => ops.insertAfter(dto, menu.value.index, alias));
  if (res === 'exists') showToast({ text: t('toast.exists', { q: `«${labelOf(alias)}»`, field: m.fieldName }) });
  closeMenu();
}

function onMenuRemove() {
  const idx = menu.value.index;
  closeMenu();
  mutate(t('hist.remove'), () => ops.removeMap(dto, idx));
  showToast({ text: t('toast.removed'), undo: true });
}

function openFieldFromMenu() {
  const m = menuMap();
  if (m) openField(ops.targetKey(m.entityClass, m.fieldName));
}

// ── Lienzo y carril ──────────────────────────────────────────────────────────
function onConnect({ alias, target }) {
  const [cls, field] = target.split('#');
  if (keyRoleOf(dto.rootEntity, cls, field)) {
    say('warn', t('ed.keyNoConnect'));
    return;
  }
  const res = mutate(t('hist.connect'), () => ops.connect(dto, alias, cls, field));
  showToast(res === 'exists' ? { text: t('toast.exists', { q: `«${labelOf(alias)}»`, field }) }
    : { text: t('toast.connected', { q: `«${labelOf(alias)}»`, field }), undo: true, open: target });
}

function toggleGroup(id) {
  if (id === 'grp:fields') view.fieldsOpen = !view.fieldsOpen;
  else if (id === 'grp:aliases') view.aliasesOpen = !view.aliasesOpen;
}

function onCanvasAction(id) {
  if (id.startsWith('legacy:')) mutate(t('hist.legacy'), () => ops.removeClass(dto, id.slice(7)));
  if (id === 'orphans') {
    const orphans = orphanAliases(dto);
    mutate(t('hist.orphans'), () => { dto.maps = dto.maps.filter((m) => !orphans.has(m.sourceAlias)); });
  }
}

/** Franja de resumen al dejar el puntero sobre un campo o una pregunta. */
function onHoverRow(r) {
  clearTimeout(peekTimer);
  if (!r || menu.value) {
    peek.value = null;
    return;
  }
  peekTimer = setTimeout(() => {
    if (r.kind === 'pill') {
      const cov = coverage(r.id, dto.sources);
      peek.value = { title: labelOf(r.id), text: t('cv.aliasPeek', { q: '', alias: r.id, k: cov.k, N: cov.N, fields: fieldNames(r.id).join(', ') || '—' }).replace(/^ · /, ''), lines: [] };
    } else if (r.kind === 'row' || r.kind === 'key') {
      const f = r.kind === 'key' ? entityFields.value.get(ops.targetKey(dto.rootEntity, r.id === 'key:id' ? entityByClass(dto.rootEntity)?.sourceIdField : entityByClass(dto.rootEntity)?.originField)) : entityFields.value.get(r.id);
      const rc = f && recipes.value.get(f.id);
      if (!f || !rc) return;
      const lines = (rc.perForm || []).slice(0, 3).map((p) => `P${p.priority} ${p.name}: ${p.uses && p.uses.length ? t('rc.pf.uses', { chain: p.uses.join(' › ') }) : t('rc.pf.' + (p.state === 'off' ? 'off' : p.state === 'missing' ? 'missing' : 'silent'))}`);
      if ((rc.perForm || []).length > 3) lines.push('…');
      peek.value = { title: f.name, text: rc.line, lines: rc.keyRows ? rc.keyRows.slice(0, 3).map((k) => `P${k.priority} ${k.name}: ${k.text}`) : lines };
    }
  }, PEEK_MS);
}

function onSourceUpdate({ questId, patch }) {
  if (Object.keys(patch).length === 1 && 'originText' in patch) ops.patchSource(dto, questId, patch);
  else mutate(t('hist.source'), () => ops.patchSource(dto, questId, patch));
}

function onSourceAdd(questId) {
  mutate(t('hist.addSource'), () => ops.addSource(dto, questId));
  railOpen.value = questId;
  showToast({ text: t('src.added', { form: formName(questId) }), undo: true });
}

function onSourceMove({ from, to }) {
  mutate(t('hist.moveSource'), () => ops.moveSource(dto, from, to));
}

function onSourceRemove(questId) {
  mutate(t('hist.removeSource'), () => ops.removeSource(dto, questId));
  if (railOpen.value === questId) railOpen.value = null;
  showToast({ text: t('src.removed', { form: formName(questId) }), undo: true });
}

// ── Entidad ──────────────────────────────────────────────────────────────────
function startChangeEntity() {
  if (dto.maps.length && !window.confirm(t('def.changeConfirm', { n: dto.maps.length }))) return;
  picking.value = true;
}

function pickEntity(cls) {
  if (dto.rootEntity && cls !== dto.rootEntity) {
    mutate(t('hist.entity'), () => {
      dto.rootEntity = cls;
      dto.maps = [];
      dto.fields = [];
    });
    showToast({ text: t('toast.entity', { name: comboName(cls) }), undo: true });
  } else {
    dto.rootEntity = cls;
  }
  picking.value = false;
  nextTick(() => canvasRef.value?.fitWidth());
}

// ── Avisos, requisitos y teclado ─────────────────────────────────────────────
function say(type, text) {
  notice.value = { type, text };
}

function showToast(tt) {
  clearTimeout(toastTimer);
  toast.value = tt;
  toastTimer = setTimeout(() => { toast.value = null; }, TOAST_MS);
}

function undo() {
  const label = history.undo();
  menu.value = null;
  if (label) showToast({ text: t('toast.undone', { what: label }), redo: true });
}

function redo() {
  const label = history.redo();
  menu.value = null;
  if (label) showToast({ text: t('toast.redone', { what: label }), undo: true });
}

/** Lleva a donde se arregla un requisito pendiente o un aviso. */
function goTo(target) {
  if (sheet.value) sheet.value = null;
  closeMenu();
  if (target.kind === 'definition') defRef.value?.open(target.focus || 'name');
  else if (target.kind === 'entity') (dto.rootEntity ? defRef.value?.open('entity') : pickerRef.value?.focus());
  else if (target.kind === 'trigger') triggerRef.value?.open();
  else if (target.kind === 'addSource') railRef.value?.openAdd();
  else if (target.kind === 'form') {
    railOpen.value = target.id;
    nextTick(() => railRef.value?.focusSource(target.id, target.focus));
  } else if (target.kind === 'field') nextTick(() => openField(target.id));
  else if (target.kind === 'canvas') canvasRef.value?.focusRow(canvas.value.fieldOrder[0]);
}

function focusFirstMatch() {
  const row = canvas.value.nodes.flatMap((n) => n.rows).find((r) => r.lit && (r.kind === 'row' || r.kind === 'key'));
  if (row) canvasRef.value?.focusRow(row.id);
}

function onKey(e) {
  const mod = e.ctrlKey || e.metaKey;
  const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) && e.target.type !== 'checkbox' && e.target.type !== 'radio';
  if (mod && e.key.toLowerCase() === 's') {
    e.preventDefault();
    if (!busy.value && dto.rootEntity) doSave();
  } else if (mod && !typing && e.key.toLowerCase() === 'z') {
    e.preventDefault();
    if (e.shiftKey) redo();
    else undo();
  } else if (mod && !typing && e.key.toLowerCase() === 'y') {
    e.preventDefault();
    redo();
  } else if (e.key === 'Escape' && !sheet.value) {
    if (menu.value) closeMenu();
    else if (legendOpen.value) legendOpen.value = false;
    else if (view.query) view.query = '';
  }
}

/** Ajusta el alto del editor a lo que queda de ventana bajo él (standalone o embebido). */
function fitHeight() {
  const el = gridRef.value;
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY;
  // El padding inferior de .vem-root reserva el sitio de la barra CONFIG fija (standalone).
  const below = parseFloat(getComputedStyle(el.closest('.vem-root')).paddingBottom) || 0;
  gridH.value = Math.max(420, Math.round(window.innerHeight - top - below));
}

// ── Carga / guardado / acciones ──────────────────────────────────────────────
function toPayload() {
  const mapped = new Set(dto.maps.map((m) => ops.targetKey(m.entityClass, m.fieldName)));
  return {
    id: dto.id, name: dto.name, description: dto.description, rootEntity: dto.rootEntity,
    triggerType: dto.triggerType, triggerCron: dto.triggerType === 'cron' ? dto.triggerCron : null,
    triggerEvery: dto.triggerType === 'interval' ? dto.triggerEvery : null,
    triggerUnit: dto.triggerType === 'interval' ? dto.triggerUnit : null,
    enabled: dto.enabled, layout: JSON.stringify({ v: 2, extensions: dto.rootEntity ? extensionsOf(dto.rootEntity) : [] }),
    sources: dto.sources, maps: dto.maps,
    fields: dto.fields.filter((f) => mapped.has(ops.targetKey(f.entityClass, f.fieldName))),
  };
}

async function load() {
  loading.value = true;
  try {
    await loadCatalog(true);
    if (props.defId) {
      const d = await getDef(props.defId);
      Object.assign(dto, d, {
        triggerCron: d.triggerCron || '0 0 3 * * *', triggerEvery: d.triggerEvery || 1, triggerUnit: d.triggerUnit || 'h',
        description: d.description || '',
      });
      ops.normalizeSources(dto);
    } else {
      dto.name = t('list.new').replace(/^\+\s*/, '');
    }
    if (dto.maps.length < 5) view.fieldsOpen = view.aliasesOpen = true;
    fitHeight();
    await nextTick();
    canvasRef.value?.fitWidth();
  } catch (e) {
    say('err', e.message);
  } finally {
    loading.value = false;
    await nextTick();
    saved.value = JSON.stringify(toPayload());
  }
}

async function act(name, fn) {
  busy.value = name;
  notice.value = null;
  try {
    await fn();
  } catch (e) {
    say('err', e.message);
  } finally {
    busy.value = '';
  }
}

const doSave = () => act('save', async () => {
  const res = await saveDef(toPayload());
  dto.id = res.id;
  saved.value = JSON.stringify(toPayload());
  serverProblems.value = res.problems;
  await loadCatalog(true);
  say('ok', t('ed.saved'));
  emit('saved', res.id);
  if (res.problems.length) {
    if (sheet.value) {
      showToast({ text: t('toast.serverProblems', { n: res.problems.length }), view: () => { sheet.value = null; toast.value = null; readyRef.value?.show(); } });
    } else {
      readyRef.value?.show();
    }
  }
});

function requireSaved() {
  if (!dto.id || dirty.value) {
    say('warn', t('ed.saveFirst'));
    return false;
  }
  return true;
}

const doRun = () => requireSaved() && act('run', async () => {
  await runDef(dto.id);
  dto.runRequested = true;
  say('ok', t('ed.runQueued'));
});

const doDelete = () => {
  if (!window.confirm(t('ed.deleteConfirm', { name: dto.name }))) return;
  act('delete', async () => {
    await deleteDef(dto.id);
    emit('back');
  });
};

watch(() => dto.name, (v) => emit('title', v), { immediate: true });
watch(notice, () => nextTick(fitHeight));
watch(dirty, (v) => {
  if (v) serverProblems.value = [];
});
watch(() => dto.rootEntity, () => nextTick(fitHeight));

onMounted(() => {
  window.addEventListener('resize', fitHeight);
  fitHeight();
  load();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', fitHeight);
  clearTimeout(peekTimer);
  clearTimeout(toastTimer);
});
</script>
